import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Alert,
} from "@mui/material";
import { Axios } from "../../../Api/axios";
import Cookie from "cookie-universal";
import { CLIENTS } from "../../../Api/Api";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";

const cookie = Cookie();
const token = cookie.get("dashboardLogin");
const userRole = cookie.get("userRole");

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#098040",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function ClientsTable() {
  const [alert, setAlert] = useState(null);
  const [alertVisible, setAlertVisible] = useState(true);
  const [clients, setClients] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editData, setEditData] = useState({
    id: null,
    title_en: "",
    title_ar: "",
    image: null,
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("id");

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await Axios.get(`/panel/${CLIENTS}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-User-Role": userRole,
        },
      });
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - clients.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...clients]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, clients]
  );

  const handleEdit = (id) => {
    const client = clients.find((row) => row.id === id);
    setEditData({
      id: client.id,
      title_en: client.title_en,
      title_ar: client.title_ar,
      image: client.image?.path || null,
    });
    setPreviewImage(client.image?.path || null);
    setIsEditing(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const preview = URL.createObjectURL(file);
      setPreviewImage(preview);
    }
  };

  const handleUpdate = async () => {
    if (!window.confirm("Are you sure you want to update this client?")) return;

    const formData = new FormData();
    formData.append("title_en", editData.title_en);
    formData.append("title_ar", editData.title_ar);
    formData.append("_method", "PUT");

    if (selectedFile) {
      formData.append("image", selectedFile);
    } else if (previewImage === null && editData.image) {
      formData.append("remove_image", true);
    }

    try {
      const response = await Axios.post(
        `/panel/${CLIENTS}/${editData.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-User-Role": userRole,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setClients(
        clients.map((client) => {
          if (client.id === editData.id) {
            return {
              ...client,
              ...response.data,
              image: response.data.image || client.image,
            };
          }
          return client;
        })
      );

      setAlert({ message: "Client updated successfully", severity: "success" });
      handleCloseEdit();
    } catch (error) {
      console.error("Error updating client:", error);
      setAlert({ message: "Error updating client", severity: "error" });
    }

    setTimeout(() => setAlertVisible(false), 2000);
    setAlertVisible(true);
  };

  const handleCloseEdit = () => {
    setSelectedFile(null);
    setPreviewImage(null);
    setIsEditing(false);
    setEditData({ id: null, title_en: "", title_ar: "", image: null });
    if (previewImage) URL.revokeObjectURL(previewImage);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;

    try {
      await Axios.delete(`/panel/${CLIENTS}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-User-Role": userRole,
        },
      });

      setClients(clients.filter((client) => client.id !== id));
      setAlert({ message: "Client deleted successfully", severity: "success" });
    } catch (error) {
      console.error("Error deleting client:", error);
      setAlert({ message: "Error deleting client", severity: "error" });
    }

    setTimeout(() => setAlertVisible(false), 2000);
    setAlertVisible(true);
  };

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      {alert && alertVisible && (
        <Alert
          severity={alert.severity}
          onClose={() => setAlertVisible(false)}
          sx={{
            transition: "opacity 1s ease-out",
            opacity: alertVisible ? 1 : 0,
          }}
        >
          {alert.message}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "id")}
                style={{ cursor: "pointer" }}
              >
                ID
                {orderBy === "id" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "id" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>

              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "title_en")}
                style={{ cursor: "pointer" }}
              >
                Title (English)
                {orderBy === "title_en" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "title_en" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>

              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "title_ar")}
                style={{ cursor: "pointer" }}
              >
                Title (Arabic)
                {orderBy === "title_ar" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "title_ar" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>
              <StyledTableCell>Image</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((client) => (
              <StyledTableRow key={client.id}>
                <StyledTableCell>{client.id}</StyledTableCell>
                <StyledTableCell>{client.title_en}</StyledTableCell>
                <StyledTableCell>{client.title_ar}</StyledTableCell>
                <StyledTableCell>
                  {client.image ? (
                    <img
                      src={client.image.path}
                      alt="Client"
                      style={{
                        width: 50,
                        height: 50,
                      }}
                      onError={(e) => {
                        e.target.src = "/placeholder-image.png";
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 50,
                        height: 50,
                        backgroundColor: "#f0f0f0",
                        borderRadius: 4,
                      }}
                    />
                  )}
                </StyledTableCell>
                <StyledTableCell>
                  <IconButton
                    onClick={() => handleEdit(client.id)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete(client.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={clients.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={isEditing} onClose={handleCloseEdit}>
        <DialogTitle>Edit Client</DialogTitle>
        <DialogContent>
          <TextField
            label="Title (English)"
            fullWidth
            value={editData.title_en}
            onChange={(e) =>
              setEditData({ ...editData, title_en: e.target.value })
            }
            sx={{ mb: 2, mt: 2 }}
          />
          <TextField
            label="Title (Arabic)"
            fullWidth
            value={editData.title_ar}
            onChange={(e) =>
              setEditData({ ...editData, title_ar: e.target.value })
            }
            sx={{ mb: 2 }}
          />

          <Box sx={{ mt: 2, display: "flex", gap: 2, flexDirection: "column" }}>
            {previewImage && (
              <img
                src={previewImage}
                alt="Preview"
                style={{
                  width: 100,
                  height: 100,
                  objectFit: "cover",
                  borderRadius: 4,
                }}
              />
            )}
            <Button
              variant="outlined"
              component="label"
              sx={{ width: "fit-content" }}
            >
              {selectedFile ? "Change Image" : "Upload Image"}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>
            {editData.image && !selectedFile && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  setPreviewImage(null);
                  setEditData((prev) => ({ ...prev, image: null }));
                }}
              >
                Remove Current Image
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} color="primary">
            Cancel
          </Button>
          <Button onClick={handleUpdate} color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
